import { authHeaders, type AuthArgs } from './authArgs.ts';

export interface ObservabilityStatus {
  enabled: boolean;
  configured: boolean;
  icmEnabled?: boolean;
  targets: { id: string; displayName: string; icmConfigured?: boolean }[];
  models: { id: string; displayName: string }[];
  defaultModelId: string | null;
  maxHistoryMessages: number;
  maxQuestionLength: number;
  maxLookbackHours: number;
}

export interface ObservabilityMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface ObservabilityRequest {
  targetId: string;
  modelId?: string;
  question: string;
  lookbackHours: number;
  history: ObservabilityMessage[];
}

export interface ObservabilityEvidence {
  tool: string;
  title: string;
  query: string;
  rows: Record<string, string | null>[];
  truncated: boolean;
  source?: 'azure-monitor' | 'icm';
  notice?: string | null;
}

export interface ObservabilityResponse {
  answer: string;
  targetId: string;
  modelId: string;
  lookbackHours: number;
  evidence: ObservabilityEvidence[];
}

export class ObservabilityError extends Error {
  readonly status: number;

  constructor(status: number) {
    const messages: Record<number, string> = {
      400: 'The question or scope is invalid. Check your selection and try again.',
      401: 'Verified sign-in is required. Sign in again; if this continues, ask an administrator to check authentication configuration.',
      403: 'You need the Admin.Observability permission to use this page.',
      404: 'Observability is disabled or unavailable in this environment.',
      409: 'The selected chat model is no longer available. Refresh availability or select another chat model and try again.',
      413: 'The request is too large. Shorten your question or start a new conversation.',
      429: 'Observability is busy. Wait a moment and try again.',
      502: 'The observability service could not complete the request. Try again later.',
      503: 'Observability is not configured. Contact an administrator.',
      504: 'The observability request timed out. Try a shorter lookback or try again.',
    };
    super(messages[status] ?? 'Could not reach the observability service. Try again later.');
    this.name = 'ObservabilityError';
    this.status = status;
  }
}

export function observabilityErrorMessage(error: unknown): string {
  return error instanceof ObservabilityError
    ? error.message
    : 'Could not complete the observability request. Try again later.';
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isInteger(value: unknown, minimum: number): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= minimum;
}

function isStatus(value: unknown): value is ObservabilityStatus {
  return isRecord(value)
    && typeof value.enabled === 'boolean'
    && typeof value.configured === 'boolean'
    && (!Object.hasOwn(value, 'icmEnabled') || typeof value.icmEnabled === 'boolean')
    && Array.isArray(value.targets)
    && value.targets.every((target) => isRecord(target)
      && typeof target.id === 'string' && target.id.length > 0
      && typeof target.displayName === 'string'
      && (!Object.hasOwn(target, 'icmConfigured') || typeof target.icmConfigured === 'boolean'))
    && Array.isArray(value.models)
    && value.models.every((model) => isRecord(model)
      && typeof model.id === 'string' && model.id.trim().length > 0
      && typeof model.displayName === 'string' && model.displayName.trim().length > 0)
    && new Set(value.models.map((model) => model.id)).size === value.models.length
    && (value.defaultModelId === null
      || (typeof value.defaultModelId === 'string'
        && value.models.some((model) => model.id === value.defaultModelId)))
    && isInteger(value.maxHistoryMessages, 0)
    && isInteger(value.maxQuestionLength, 1)
    && isInteger(value.maxLookbackHours, 1);
}

function isResponse(value: unknown): value is ObservabilityResponse {
  return isRecord(value)
    && typeof value.answer === 'string'
    && typeof value.targetId === 'string'
    && typeof value.modelId === 'string' && value.modelId.trim().length > 0
    && isInteger(value.lookbackHours, 1)
    && Array.isArray(value.evidence)
    && value.evidence.every((item) => isRecord(item)
      && typeof item.tool === 'string'
      && typeof item.title === 'string'
      && typeof item.query === 'string'
      && typeof item.truncated === 'boolean'
      && (!Object.hasOwn(item, 'source') || item.source === 'azure-monitor' || item.source === 'icm')
      && (!Object.hasOwn(item, 'notice') || item.notice === null
        || (typeof item.notice === 'string' && item.notice.length <= 1024))
      && Array.isArray(item.rows)
      && item.rows.every((row) => isRecord(row)
        && Object.values(row).every((cell) => cell === null || typeof cell === 'string')));
}

async function request<T>(
  path: string,
  args: AuthArgs,
  signal: AbortSignal,
  validate: (value: unknown) => value is T,
  body?: ObservabilityRequest,
): Promise<T> {
  try {
    signal.throwIfAborted();
    const response = await fetch(`/api/v1/admin/observability/${path}`, {
      method: body ? 'POST' : 'GET',
      headers: authHeaders(args),
      signal,
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    signal.throwIfAborted();
    if (!response.ok) throw new ObservabilityError(response.status);
    const value: unknown = await response.json();
    signal.throwIfAborted();
    if (!validate(value)) throw new ObservabilityError(502);
    return value;
  } catch (error) {
    signal.throwIfAborted();
    // Never surface arbitrary transport exceptions or upstream response bodies.
    throw error instanceof ObservabilityError ? error : new ObservabilityError(0);
  }
}

export function getObservabilityStatus(args: AuthArgs, signal: AbortSignal): Promise<ObservabilityStatus> {
  return request('status', args, signal, isStatus);
}

export function sendObservabilityQuestion(
  body: ObservabilityRequest,
  args: AuthArgs,
  signal: AbortSignal,
): Promise<ObservabilityResponse> {
  return request('chat', args, signal, isResponse, body);
}
